/**
 * 都道府県制覇マップ
 *
 * 47 都道府県を地方ごとのグリッドで表示し、訪問回数に応じて濃さを変える。
 * タップすると訪問した市区町村の内訳をボトムシートで表示する。
 */

import { format } from 'date-fns'
import { useMemo, useState } from 'react'

import type { ConquestPrefecture } from '@/api/types/trip'
import { BottomSheet } from '@/components/ui/bottom-sheet'
import { Progress } from '@/components/ui/progress'
import { normalizePrefecture, REGIONS, shortPrefectureName, TOTAL_PREFECTURE_COUNT } from '@/lib/prefectures'
import { cn } from '@/lib/utils'

type Props = {
  prefectures: ConquestPrefecture[]
}

/** 訪問回数に応じた 4 段階の塗り */
const levelClass = (visitCount: number): string => {
  if (visitCount === 0) return 'bg-muted text-muted-foreground/60'
  if (visitCount < 5) return 'bg-emerald-200 text-emerald-950 dark:bg-emerald-900 dark:text-emerald-50'
  if (visitCount < 20) return 'bg-emerald-400 text-emerald-950 dark:bg-emerald-700 dark:text-emerald-50'
  return 'bg-emerald-600 text-white dark:bg-emerald-500'
}

export const ConquestMapView = ({ prefectures }: Props) => {
  const [selected, setSelected] = useState<ConquestPrefecture | null>(null)

  // 「東京」「東京都」の表記ゆれを吸収してマスタと突き合わせる
  const byName = useMemo(() => {
    const map = new Map<string, ConquestPrefecture>()
    for (const pref of prefectures) {
      map.set(normalizePrefecture(pref.prefecture), pref)
    }
    return map
  }, [prefectures])

  const conqueredCount = useMemo(
    () => REGIONS.flatMap((r) => r.prefectures).filter((name) => byName.has(normalizePrefecture(name))).length,
    [byName],
  )
  const totalCities = useMemo(() => prefectures.reduce((sum, pref) => sum + pref.cities.length, 0), [prefectures])

  const percent = Math.round((conqueredCount / TOTAL_PREFECTURE_COUNT) * 100)

  return (
    <div className="space-y-6">
      <div className="rounded-xl border bg-card p-4">
        <div className="mb-2 flex items-baseline justify-between">
          <p className="text-sm text-muted-foreground">都道府県制覇</p>
          <p className="text-sm text-muted-foreground">{totalCities} 市区町村</p>
        </div>
        <p className="mb-3 text-2xl font-bold">
          {conqueredCount}
          <span className="ml-1 text-base font-normal text-muted-foreground">/ {TOTAL_PREFECTURE_COUNT} 県</span>
          <span className="ml-2 text-base font-normal text-emerald-600">{percent}%</span>
        </p>
        <Progress value={percent} />
      </div>

      {REGIONS.map((region) => (
        <div key={region.name}>
          <h3 className="mb-2 text-sm font-semibold text-muted-foreground">{region.name}</h3>
          <div className="grid grid-cols-4 gap-2">
            {region.prefectures.map((name) => {
              const data = byName.get(normalizePrefecture(name))
              const visitCount = data?.visit_count ?? 0
              return (
                <button
                  key={name}
                  type="button"
                  disabled={!data}
                  onClick={() => data && setSelected(data)}
                  className={cn(
                    'flex aspect-square! flex-col items-center justify-center gap-0.5 rounded-lg p-1! text-xs font-medium transition-transform',
                    levelClass(visitCount),
                    data && 'active:scale-95',
                  )}
                >
                  <span className="wrap-break-word text-center leading-tight">{shortPrefectureName(name)}</span>
                  {visitCount > 0 && <span className="text-[10px] opacity-80">{visitCount}</span>}
                </button>
              )
            })}
          </div>
        </div>
      ))}

      <BottomSheet
        title={selected?.prefecture ?? ''}
        open={selected !== null}
        onOpenChange={(open) => !open && setSelected(null)}
        snapPoints={[0.7]}
      >
        {selected && (
          <div className="space-y-4 px-4 pb-8">
            <div className="flex items-center gap-4 text-sm text-muted-foreground">
              <span>{selected.visit_count} 回訪問</span>
              <span>{selected.cities.length} 市区町村</span>
            </div>
            {selected.first_visited_at && (
              <p className="text-xs text-muted-foreground">
                初訪問: {format(new Date(selected.first_visited_at), 'yyyy/MM/dd')}
                {selected.last_visited_at && ` ・ 最終: ${format(new Date(selected.last_visited_at), 'yyyy/MM/dd')}`}
              </p>
            )}
            <ul className="divide-y rounded-lg border">
              {selected.cities.map((city) => (
                <li key={city.city} className="flex items-center justify-between px-3 py-2 text-sm">
                  <span>{city.city}</span>
                  <span className="text-muted-foreground">{city.visit_count}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </BottomSheet>
    </div>
  )
}
